import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Shield, Search, Download, RefreshCw, ChevronLeft, ChevronRight, ChevronDown, User, Activity } from 'lucide-react';
import api from '../../api/apiClient';
import { DashboardSkeleton } from '../../components/design-system/EmptyState';
import { EmptyState } from '../../components/design-system/EmptyState';

interface AuditEntry {
  id: string;
  actor: string;
  actorRole: string;
  action: string;
  resourceType: string;
  resourceId: string;
  details: string;
  createdAt: string;
}

interface PagedResponse {
  content: AuditEntry[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

const ROLE_COLORS: Record<string, string> = {
  ADMIN:      'bg-coral-100 text-coral-700',
  AGENT:      'bg-teal-100 text-teal-700',
  OWNER:      'bg-info-100 text-info-700',
  RENTER:     'bg-ok-100 text-ok-700',
  CONTRACTOR: 'bg-warn-100 text-warn-700',
  SYSTEM:     'bg-ink-100 text-ink-600',
};

function formatDate(iso: string) {
  const d = new Date(iso);
  return d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

export default function AdminAuditLog() {
  const [actor,  setActor]  = useState('');
  const [action, setAction] = useState('');
  const [date,   setDate]   = useState('');
  const [page,   setPage]   = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) =>
    setExpanded(prev => { const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s; });
  const PAGE_SIZE = 50;

  // Build query params
  const params = new URLSearchParams();
  if (actor)  params.set('actor',  actor);
  if (action) params.set('action', action);
  if (date)   params.set('date',   date);
  params.set('page', String(page));
  params.set('size', String(PAGE_SIZE));

  const isFiltered = actor || action || date;
  const endpoint = isFiltered ? `/admin/audit/filter?${params}` : `/admin/audit?${params}`;

  const { data, isLoading, isError, refetch, isFetching } = useQuery<PagedResponse>({
    queryKey: ['admin', 'audit', actor, action, date, page],
    queryFn: () => api.get<PagedResponse>(endpoint),
    staleTime: 30_000,
  });

  const handleExport = () => {
    const ep = isFiltered
      ? `/admin/audit/export?${new URLSearchParams({ actor, action, date }).toString()}`
      : '/admin/audit/export';
    // Trigger download via hidden anchor
    api.get<Blob>(ep).then(() => {
      const url = `/api${ep}`;
      const a = document.createElement('a');
      a.href = url; a.download = 'audit_log.csv'; a.click();
    });
  };

  if (isLoading) return <DashboardSkeleton />;

  const entries = data?.content ?? [];

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-[22px] font-semibold text-ink-900 flex items-center gap-2">
            <Shield size={20} className="text-forest-900" />
            Audit Log
          </h1>
          <p className="text-[13px] text-ink-500 mt-0.5">
            {data ? `${data.totalElements.toLocaleString()} entries` : 'Platform-wide activity trail'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="flex items-center gap-1.5 px-3 py-2 text-[13px] text-ink-600 bg-card border border-ink-200 rounded-10 hover:bg-ink-50 transition-colors disabled:opacity-50"
          >
            <RefreshCw size={14} className={isFetching ? 'animate-spin' : ''} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 px-3 py-2 text-[13px] text-white bg-forest-900 rounded-10 hover:bg-forest-800 transition-colors"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-12 border border-ink-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              value={actor}
              onChange={e => { setActor(e.target.value); setPage(0); }}
              placeholder="Filter by actor email…"
              className="w-full pl-8 pr-3 py-2 text-[13px] border border-ink-200 rounded-10 focus:outline-none focus:ring-2 focus:ring-forest-900 bg-white"
            />
          </div>
          <div className="relative">
            <Activity size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
            <input
              value={action}
              onChange={e => { setAction(e.target.value); setPage(0); }}
              placeholder="Filter by action…"
              className="w-full pl-8 pr-3 py-2 text-[13px] border border-ink-200 rounded-10 focus:outline-none focus:ring-2 focus:ring-forest-900 bg-white"
            />
          </div>
          <input
            type="date"
            value={date}
            onChange={e => { setDate(e.target.value); setPage(0); }}
            className="px-3 py-2 text-[13px] border border-ink-200 rounded-10 focus:outline-none focus:ring-2 focus:ring-forest-900 bg-white"
          />
        </div>
      </div>

      {/* Table */}
      {isError ? (
        <div className="bg-danger-50 border border-danger-200 text-danger-700 rounded-12 px-5 py-4 text-[13px]">
          Failed to load audit log. Ensure the backend is running and you have ADMIN access.
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={<Shield size={36} />}
          title="No audit entries"
          description={isFiltered ? 'No entries match your filters.' : 'No audit events have been recorded yet.'}
        />
      ) : (
        <>
          <div className="bg-card rounded-12 border border-ink-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-ink-200 bg-ink-50">
                    <th className="w-8 px-2 py-3" />
                    <th className="text-left px-4 py-3 font-semibold text-ink-600 whitespace-nowrap">Time</th>
                    <th className="text-left px-4 py-3 font-semibold text-ink-600 whitespace-nowrap">Actor</th>
                    <th className="text-left px-4 py-3 font-semibold text-ink-600 whitespace-nowrap">Role</th>
                    <th className="text-left px-4 py-3 font-semibold text-ink-600 whitespace-nowrap">Action</th>
                    <th className="text-left px-4 py-3 font-semibold text-ink-600 whitespace-nowrap">Resource</th>
                    <th className="text-left px-4 py-3 font-semibold text-ink-600">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-100">
                  {entries.map(entry => {
                    const isOpen = expanded.has(entry.id);
                    return (
                      <>
                        <tr
                          key={entry.id}
                          className="hover:bg-ink-50/60 transition-colors cursor-pointer"
                          onClick={() => toggleExpand(entry.id)}
                        >
                          <td className="px-2 py-3 text-ink-400">
                            <ChevronDown size={14} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
                          </td>
                          <td className="px-4 py-3 text-ink-500 whitespace-nowrap">{formatDate(entry.createdAt)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-1.5">
                              <User size={12} className="text-ink-400 flex-none" />
                              <span className="text-ink-800 font-medium truncate max-w-[160px]">{entry.actor}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${ROLE_COLORS[entry.actorRole] ?? ROLE_COLORS.SYSTEM}`}>
                              {entry.actorRole}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <code className="text-[12px] text-forest-800 bg-forest-50 px-1.5 py-0.5 rounded-6">
                              {entry.action}
                            </code>
                          </td>
                          <td className="px-4 py-3 text-ink-600 whitespace-nowrap">
                            {entry.resourceType}
                            {entry.resourceId ? <span className="text-ink-400"> #{entry.resourceId}</span> : null}
                          </td>
                          <td className="px-4 py-3 text-ink-500 max-w-xs truncate">{entry.details}</td>
                        </tr>
                        {isOpen && (
                          <tr key={`${entry.id}-detail`} className="bg-ink-50/40">
                            <td />
                            <td colSpan={6} className="px-4 py-3">
                              <div className="text-[12px] text-ink-600 font-mono whitespace-pre-wrap break-all bg-white border border-ink-200 rounded-8 p-3">
                                {entry.details || <span className="text-ink-400 italic">No details recorded</span>}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          {data && data.totalPages > 1 && (
            <div className="flex items-center justify-between text-[13px] text-ink-600">
              <span>
                Page {data.number + 1} of {data.totalPages} ({data.totalElements.toLocaleString()} total)
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={data.number === 0}
                  className="p-1.5 rounded-8 hover:bg-ink-100 disabled:opacity-40 transition-colors"
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  onClick={() => setPage(p => Math.min(data.totalPages - 1, p + 1))}
                  disabled={data.number >= data.totalPages - 1}
                  className="p-1.5 rounded-8 hover:bg-ink-100 disabled:opacity-40 transition-colors"
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
